import { computed, ref, unref, type Ref } from "vue";
import { normalizeError } from "@/queries/core/errors";
import { queryCache } from "@/queries/core/queryCache";
import type { QueryError, QueryMatcher } from "@/queries/core/types";
import type { MutationOptions, MutationStatus, UseMutationReturn } from "./types";

const resolveInvalidateMatchers = (
  invalidate?: MutationOptions<unknown, unknown>["invalidateQueries"],
): QueryMatcher[] => {
  if (!invalidate) return [];

  const resolved = unref(invalidate as QueryMatcher | QueryMatcher[] | undefined);
  if (!resolved) return [];

  return Array.isArray(resolved) ? resolved : [resolved];
};

export function createMutation<TData, TVariables>(
  options: MutationOptions<TData, TVariables>,
): UseMutationReturn<TData, TVariables> {
  const status = ref<MutationStatus>("idle");
  const error = ref<QueryError | null>(null);
  // См. тот же приём в `queries/core/createQuery`: `ref<T>()` для несвязанного
  // дженерика типизируется через `UnwrapRef`, приведение — только в типе.
  const data = ref<TData | null>(null) as Ref<TData | null>;

  const isPending = computed(() => status.value === "pending");
  const isSuccess = computed(() => status.value === "success");
  const isError = computed(() => status.value === "error");

  const execute = async (variables: TVariables): Promise<TData> => {
    status.value = "pending";
    error.value = null;

    try {
      const result = await options.mutationFn(variables);
      data.value = result;
      status.value = "success";
      options.onSuccess?.(result, variables);

      const matchers = resolveInvalidateMatchers(options.invalidateQueries);
      matchers.forEach((matcher) => queryCache.invalidate(matcher));

      options.onSettled?.(result, null, variables);
      return result;
    } catch (err) {
      const normalized = normalizeError(err);
      error.value = normalized;
      status.value = "error";
      options.onError?.(normalized, variables);
      options.onSettled?.(null, normalized, variables);
      throw normalized;
    }
  };

  const mutate = (variables: TVariables) => {
    void execute(variables);
  };

  const mutateAsync = (variables: TVariables) => execute(variables);

  const reset = () => {
    status.value = "idle";
    error.value = null;
    data.value = null;
  };

  return {
    status,
    isPending,
    isSuccess,
    isError,
    error,
    data,
    mutate,
    mutateAsync,
    reset,
  };
}

